import { useMemo } from 'react';
import { contextualParams, getApiClientErrorMessageFromError, hydraUiApi, useHydraUIAPIQuery } from '@/services/api';
import { AccountFeatureFlag, FEATURE_FLAG_DEFAULTS } from '@/services/auth/feature-flags';
import { DEV_HELPER_TABS, openDevHelperTab } from '@/services/dev-helper/store';
import type { LocalCommand } from './local-commands';
import { matchesLocalCommand } from './utils/search-commands';

const DRYAD_FLAGS = Object.values(AccountFeatureFlag).filter((flag) => flag.startsWith('dryad-'));

// Flag names can match a query broadly (e.g. "dryad" matches ~30 flags), so cap
// how many single-flag toggles we surface at once.
const MAX_FLAG_COMMANDS = 10;

// Same local-only gate as the dev-helper's debug tabs.
const isLocalEnv = Boolean(import.meta.env.DEV) || import.meta.env.VITE_ENABLE_EXTERNAL_UI_OVERRIDES === 'true';

async function updateFlags(accountId: string, flags: Record<string, boolean>): Promise<void> {
  await hydraUiApi.accounts.updateFeatureFlags({
    params: { path: { account_id: accountId } },
    body: flags,
  });
  window.location.reload();
}

function alertUpdateFailed(error: unknown): void {
  // Dev-only tool; alert avoids wiring snackbars outside the provider tree.
  window.alert(`Failed to update feature flags: ${getApiClientErrorMessageFromError(error) ?? String(error)}`);
}

const devHelperTabCommands: LocalCommand[] = DEV_HELPER_TABS.map(({ value, text }) => ({
  id: `dev:dev-helper-tab:${value}`,
  label: `Dev helper: ${text}`,
  icon: 'editor-code',
  description: `Open the ${text} tab in the dev helper panel.`,
  keywords: ['dev', 'helper', 'tab', 'debug', 'panel', ...text.toLowerCase().split(' ')],
  onSelect: () => {
    openDevHelperTab(value);
  },
}));

type DevCommandsOptions = {
  includeEmptyQuery?: boolean;
};

export function useDevCommands(query: string, options: DevCommandsOptions = {}): LocalCommand[] {
  const { includeEmptyQuery = false } = options;
  const params = contextualParams();

  const { data: featureFlags } = useHydraUIAPIQuery({
    queryKey: ['getFeatureFlags', params.accountId],
    queryFn: ({ signal }) =>
      hydraUiApi.accounts.getFeatureFlags({ params: { path: { account_id: String(params.accountId!) } }, signal }),
    queryConfig: {
      enabled: isLocalEnv && params.accountId != null,
      select: (data) => data.featureFlags ?? {},
    },
  });

  const accountId = params.accountId;

  return useMemo(() => {
    if (!isLocalEnv || accountId == null) return [];
    if (!includeEmptyQuery && query.trim().length === 0) return [];
    // The PUT body must carry the full current flag map — without it we could
    // wipe every non-dryad flag. No command until the flags query resolves.
    if (featureFlags == null) return [];

    const allEnabled = DRYAD_FLAGS.every((flag) => featureFlags[flag] === true);

    const commands: LocalCommand[] = [
      {
        id: 'dev:toggle-dryad-globally',
        label: allEnabled ? 'Disable Dryad globally' : 'Enable Dryad globally',
        icon: 'skills',
        description: allEnabled
          ? 'Turn every registered Dryad feature flag off and reload.'
          : 'Turn every registered Dryad feature flag on and reload.',
        keywords: ['dryad', 'enable', 'disable', 'toggle', 'feature', 'flags', 'condition', 'builder', 'dev'],
        onSelect: async () => {
          try {
            const dryadFlags = Object.fromEntries(DRYAD_FLAGS.map((flag) => [flag, !allEnabled]));
            await updateFlags(String(accountId), { ...featureFlags, ...dryadFlags });
          } catch (error) {
            alertUpdateFailed(error);
          }
        },
      },
      ...devHelperTabCommands,
    ].filter(matchesLocalCommand(query));

    // Single-flag toggles: every flag known to hydra or already set on the
    // account, matched against the query. Query-gated so command mode's empty
    // query doesn't list a hundred flags.
    if (query.trim().length > 0) {
      const knownFlags = { ...FEATURE_FLAG_DEFAULTS, ...featureFlags };
      const flagCommands = Object.entries(knownFlags)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(
          ([flag, enabled]): LocalCommand => ({
            id: `dev:toggle-flag:${flag}`,
            label: `${enabled ? 'Disable' : 'Enable'} flag: ${flag}`,
            icon: 'skills',
            description: `Turn ${enabled ? 'off' : 'on'} the ${flag} feature flag for this account and reload.`,
            keywords: ['feature', 'flag', 'toggle', flag, ...flag.split(/[-_]/)],
            onSelect: async () => {
              try {
                await updateFlags(String(accountId), { ...featureFlags, [flag]: !enabled });
              } catch (error) {
                alertUpdateFailed(error);
              }
            },
          }),
        )
        .filter(matchesLocalCommand(query))
        .slice(0, MAX_FLAG_COMMANDS);

      commands.push(...flagCommands);
    }

    return commands;
  }, [accountId, featureFlags, includeEmptyQuery, query]);
}
